import React, { forwardRef, memo, useEffect, useRef, useState } from 'react'
import { DS } from '../../design-system'
import DatePickerWeb from 'react-datepicker'

import 'react-datepicker/dist/react-datepicker.css'

import { translate } from '../../i18n'

const POPPER_CLASS = 'scheduled-tournament-datepicker-popper'
import { ALLOWED_MINUTES_OFFSET, getNextMinimumValidDateTime } from '@ppwr/poker'
import { TextInput } from 'react-native'

interface DateTimePickersProps {
  value: Date
  onChange: (date: Date) => void
  isDisabled?: boolean
}

const disabledOpacity = 0.5

// biome-ignore lint/suspicious/noExplicitAny: react-datepicker input props
const CustomInput = forwardRef((props: any, ref) => {
  const disabled = props.disabled as boolean | undefined
  return (
    <TextInput
      {...props}
      ref={ref}
      editable={false}
      style={{
        height: 48,
        backgroundColor: disabled ? DS.palette['Brand/Light Grey'] : DS.palette['Global/White'],
        borderRadius: 5,
        paddingLeft: 16,
        paddingRight: 12,
        marginTop: 6,
        borderColor: disabled ? DS.palette['Brand/Light Grey'] : DS.palette['Global/Black'],
        borderWidth: 1,
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        opacity: disabled ? 0.8 : 1,
        ...DS.typography['Body Medium/Regular']
      }}
    />
  )
})

const DateTimePickersComp = ({ value, onChange, isDisabled = false }: DateTimePickersProps) => {
  const nextValidTime = getNextMinimumValidDateTime()
  const [date, setDate] = useState(value ?? nextValidTime)
  const inputRef = useRef(null)

  // Sync from parent when value changes (e.g. loading for edit). Compare by time to avoid update loops.
  useEffect(() => {
    if (value) {
      setDate((prev) => (value.getTime() === prev.getTime() ? prev : value))
    }
  }, [value])

  useEffect(() => {
    const checkNextAvailableSchedule = () => {
      const now = new Date()
      const nextMinutes = Math.ceil(date.getTime() / (1000 * 60))
      const diff = Math.ceil(nextMinutes - now.getTime() / (1000 * 60))

      if (diff <= ALLOWED_MINUTES_OFFSET) {
        const nextValidDateTime = getNextMinimumValidDateTime()
        setDate(nextValidDateTime)
        onChange(nextValidDateTime)
      }
    }

    checkNextAvailableSchedule()

    const intervalID = setInterval(() => {
      checkNextAvailableSchedule()
    }, 1000)

    return () => clearInterval(intervalID)
  }, [date, onChange])

  return (
    <React.Fragment>
      <style>{`
        .${POPPER_CLASS} {
          z-index: 10000 !important;
        }
      `}</style>
      <DS.Frame
        row
        gap="16"
        style={{
          flex: 1,
          ...(isDisabled ? { opacity: disabledOpacity, pointerEvents: 'none' as const } : {})
        }}
      >
        <DS.Frame gap="8" style={{ flex: 1 }}>
          <DS.Text variant="Body Medium/Medium" color={isDisabled ? 'Brand/Light Grey' : 'Global/Black'}>
            {translate('createTournamentScreen.date')}
          </DS.Text>
          <DatePickerWeb
            selected={date}
            onChange={(selectedDate) => {
              if (selectedDate) {
                setDate(selectedDate)
                onChange(selectedDate)
              }
            }}
            dateFormat={'EEE MMM d'}
            customInput={<CustomInput ref={inputRef} disabled={isDisabled} />}
            popperClassName={POPPER_CLASS}
            disabled={isDisabled}
          />
        </DS.Frame>
        <DS.Frame gap="8" style={{ flex: 1 }}>
          <DS.Text variant="Body Medium/Medium" color={isDisabled ? 'Brand/Light Grey' : 'Global/Black'}>
            {translate('createTournamentScreen.time')}
          </DS.Text>
          <DatePickerWeb
            selected={date}
            onChange={(selectedDate) => {
              if (selectedDate) {
                setDate(selectedDate)
                onChange(selectedDate)
              }
            }}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="h:mm aa"
            customInput={<CustomInput ref={inputRef} disabled={isDisabled} />}
            popperClassName={POPPER_CLASS}
            disabled={isDisabled}
          />
        </DS.Frame>
      </DS.Frame>
    </React.Fragment>
  )
}

export const DateTimePickers = memo(DateTimePickersComp)
